<template>
  <NodeViewWrapper as="span" class="resizableImageWrap" :class="{ selected: selected }" :draggable="true" data-drag-handle>
    <img
      ref="imgEl"
      :src="node.attrs.src"
      :alt="node.attrs.alt || ''"
      :width="currentWidth || undefined"
      :height="currentHeight || undefined"
      :style="imgStyle"
      @click="selectNode"
    />
    <!-- Invisible edge handles (only active when selected) -->
    <template v-if="selected">
      <div class="resizeEdge resizeEdge--r" @pointerdown.prevent="(e) => onResizeStart(e, 'horizontal')" />
      <div class="resizeEdge resizeEdge--b" @pointerdown.prevent="(e) => onResizeStart(e, 'vertical')" />
      <div class="resizeEdge resizeEdge--br" @pointerdown.prevent="(e) => onResizeStart(e, 'diagonal')" />
    </template>
  </NodeViewWrapper>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { NodeViewWrapper } from '@tiptap/vue-3'

type ResizeMode = 'horizontal' | 'vertical' | 'diagonal'

const props = defineProps<{
  node: any
  updateAttributes: (attrs: Record<string, any>) => void
  selected: boolean
  selectNode: () => void
}>()

const imgEl = ref<HTMLImageElement | null>(null)
const currentWidth = ref<number | null>(props.node.attrs.width || null)
const currentHeight = ref<number | null>(props.node.attrs.height || null)

const imgStyle = computed(() => {
  const s: Record<string, string> = { maxWidth: '100%', cursor: 'pointer' }
  if (currentWidth.value) s.width = currentWidth.value + 'px'
  if (currentHeight.value) s.height = currentHeight.value + 'px'
  return s
})

let startX = 0
let startY = 0
let startWidth = 0
let startHeight = 0
let aspectRatio = 1
let activeMode: ResizeMode = 'diagonal'

function onResizeStart(e: PointerEvent, mode: ResizeMode) {
  activeMode = mode
  startX = e.clientX
  startY = e.clientY
  const img = imgEl.value
  startWidth = img?.offsetWidth || currentWidth.value || 200
  startHeight = img?.offsetHeight || currentHeight.value || 200
  const naturalW = img?.naturalWidth || 1
  const naturalH = img?.naturalHeight || 1
  aspectRatio = naturalH / naturalW

  document.addEventListener('pointermove', onResizeMove)
  document.addEventListener('pointerup', onResizeEnd)
}

function onResizeMove(e: PointerEvent) {
  const dx = e.clientX - startX
  const dy = e.clientY - startY

  if (activeMode === 'horizontal') {
    currentWidth.value = Math.max(32, startWidth + dx)
    currentHeight.value = Math.round(currentWidth.value * aspectRatio)
  } else if (activeMode === 'vertical') {
    currentHeight.value = Math.max(32, startHeight + dy)
    currentWidth.value = Math.round(currentHeight.value / aspectRatio)
  } else {
    // diagonal — use horizontal delta, maintain aspect ratio
    currentWidth.value = Math.max(32, startWidth + dx)
    currentHeight.value = Math.round(currentWidth.value * aspectRatio)
  }
}

function onResizeEnd() {
  document.removeEventListener('pointermove', onResizeMove)
  document.removeEventListener('pointerup', onResizeEnd)
  if (currentWidth.value) {
    props.updateAttributes({
      width: currentWidth.value,
      height: currentHeight.value,
    })
  }
}
</script>

<style scoped>
.resizableImageWrap {
  display: inline-block;
  position: relative;
  line-height: 0;
}
.resizableImageWrap img {
  border: 2px solid transparent;
  border-radius: 2px;
}
.resizableImageWrap.selected img {
  border-color: var(--color-accent, var(--accent, #059669));
}

/* Invisible resize edges — cursor changes on hover */
.resizeEdge {
  position: absolute;
  z-index: 1;
}
.resizeEdge--r {
  top: 0;
  right: 0;
  width: 6px;
  height: 100%;
  cursor: ew-resize;
}
.resizeEdge--b {
  bottom: 0;
  left: 0;
  width: 100%;
  height: 6px;
  cursor: ns-resize;
}
.resizeEdge--br {
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  cursor: nwse-resize;
}
</style>
